/* eslint-disable @typescript-eslint/no-empty-interface */
import {
    SimplePaletteColorOptions,
    experimental_extendTheme as extendTheme,
    lighten,
} from "@mui/material/styles";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import type {} from "@mui/material/themeCssVarsAugmentation";
import type {} from "@mui/x-date-pickers-pro/themeAugmentation";

// REMEMBER TO UPDATE WEB PROJECT TOO

/**
 * Theme and styling guidelines:
 *
 * This config is mostly for styling MUI components, but the CSS variables can also be accessed in CSS and used in other
 * components. Accessing theme variables:
 * In MUI components: sx={{ color: "primary.main" }}
 * In LESS: color: @colorPrimaryMain (needs to be set in "variables.less")
 * In CSS: color: var(--palette-primary-main)
 *
 * When creating new components, override MUI defaults or create a new variant when possible.
 *
 * Add new colors and other values here, instead of hard coding them in template or CSS. Migrate old styles here as well.
 * If the theme config API doesn't allow overriding a property, use "mui.less" instead.
 *
 * Add new variables to "variables.less" to make them easier to access in LESS using intellisense. To create accent
 * colors, use MUI's color tool. Be careful not to use Bootstrap's CSS variables, which you can see in the html element.
 *
 * For layout styles, use Grid2 from the Material UI. Check the docs for flexbox props.
 *
 * For utility classes, refer to Bootstrap's docs and the "utility.less" file. Add a new class if it doesn't already
 * exist. Make a custom component class (in "components.less") over utility classes when creating a commonly used
 * component to avoid boilerplate, if the component can be easily identified by its name or if the template gets too
 * bloated. If the component is specific to a certain module, create a module LESS file instead, under
 * "cssSource/less/module". Keep in mind that these are unrelated to React's module scope, since it doesn't support
 * LESS, but you get access to variables. See the "cssSource/less" directory for other styles. Avoid inline styles,
 * including the sx prop, for styles than can be defined using utility classes.
 *
 * These rules should be enforced in code review.
 *
 * Docs: (potentially outdated)
 * - Bootstrap Utilities: https://getbootstrap.com/docs/4.0/utilities
 * - MUI Components: https://mui.com/material-ui/react-autocomplete/
 * - MUI Theme: https://mui.com/material-ui/customization/theming/
 * - MUI Color Tool: https://m2.material.io/resources/color/
 */

// Custom variants
declare module "@mui/material/Button" {
    interface ButtonPropsVariantOverrides {
        save: true;
        primary: true;
    }
}

const colors = {
    primary: {
        light: "#63a4ff",
        main: "#1976d2",
        dark: "#033062",
    },
    secondary: {
        light: "#89c62b",
        main: "#50b027",
        dark: "#0b8000",
    },
    blue: {
        light: "#63a4ff",
        main: "#1976d2",
        dark: "#033062",
        contrastText: "#ffffff",
    },
    red: {
        light: "#ff5b56",
        main: "#ff022c",
        dark: "#c30003",
    },
    yellow: {
        light: "#fff94f",
        main: "#e2c603",
        dark: "#ee7316",
    },
    orange: {
        main: "#ee7316",
    },
    green: {
        light: "#89c62b",
        main: "#50b027",
        dark: "#0b8000",
        contrastText: "#ffffff",
    },
    gray: {
        light: "#eeeeee",
        main: "#828282",
        dark: "#e3e3e3",
    },
    lightBlue: {
        main: "#e4f7ff",
    },
    lightGreen: {
        main: lighten("#89c62b", 0.75),
    },
    grayLight: {
        main: "e3e3e3",
    },
    light: {
        main: "#f8f8f8",
    },
    white: {
        main: "#ffffff",
    },
    dark: {
        main: "#000000",
    },
    purple: {
        main: "#902bc6",
    },
    fuchsia: {
        main: "#ff02e2",
    },
    brown: {
        main: "#a06a3c",
    },
    // secondary table header
    grayBlue: {
        main: "#425980",
    },
    // odd table row
    lightGrayBlue: {
        main: "#e7eaef",
    },
};

type ColorsType = typeof colors;

// Allows custom colors for mui components.
// They always use the "main" property of a color.
type ColorOverrideFlags = {
    [Property in keyof typeof colors]: true;
};

// For each component that wants to use custom colors, this augmentation is needed.
declare module "@mui/material" {
    interface ButtonPropsColorOverrides extends ColorOverrideFlags {}
    interface IconButtonPropsColorOverrides extends ColorOverrideFlags {}
    interface SwitchPropsColorOverrides extends ColorOverrideFlags {}
    interface SvgIconPropsColorOverrides extends ColorOverrideFlags {}
    interface ChipPropsColorOverrides extends ColorOverrideFlags {}

    interface IconButtonPropsSizeOverrides {
        xmall: true;
    }
}

declare module "@mui/material/styles" {
    interface Palette extends ColorsType {}
}

const typography: TypographyOptions = {
    body1: {
        fontFamily: "Roboto, -apple-system, BlinkMacSystemFont",
        fontSize: 16,
    },
    body2: {
        fontFamily: "Roboto, -apple-system, BlinkMacSystemFont",
        fontSize: 14,
    },
    h1: {
        fontFamily: "Roboto, -apple-system, BlinkMacSystemFont",
        fontSize: 26,
        fontWeight: "500",
        color: colors.blue.dark,
    },
    h2: {
        fontFamily: "Roboto, -apple-system, BlinkMacSystemFont",
        fontSize: 22,
        fontWeight: "bold",
        color: colors.blue.dark,
    },
    h3: {
        fontFamily: "Roboto, -apple-system, BlinkMacSystemFont",
        fontSize: 20,
        fontWeight: "bold",
        color: colors.blue.dark,
    },
    h4: {
        fontSize: 18,
        fontWeight: "bold",
        color: colors.blue.dark,
    },
    h5: {
        fontSize: 16,
        fontWeight: "bold",
        color: colors.blue.dark,
    },
    subtitle1: {
        fontWeight: "bold",
    },
    subtitle2: {
        fontWeight: "bold",
    },
};

// Styling related constants for consistency
interface StyleConstants {
    fieldWidth: number;
}

export const STYLE_CONSTANTS: StyleConstants = {
    fieldWidth: 500,
} as const;

// get reference with correct type, so we get a compiler error if colors are not proper.
const customColors: Record<string, SimplePaletteColorOptions> = colors;

// This is extendTheme, not createTheme.
// Check the docs (which are very bad) for what you can do.
export const theme = extendTheme(
    {
        colorSchemes: {
            light: {
                palette: {
                    // Override MUI defaults
                    primary: colors.primary, // becomes --palette-primary-light, etc.
                    error: colors.red,
                    warning: colors.yellow,
                    success: colors.green,
                    text: {
                        primary: colors.dark.main,
                        secondary: colors.gray.main,
                    },
                    // Custom values. These don't override defaults but can be used in code and CSS.
                    ...customColors,
                },
            },
        },
        typography,
        components: {
            MuiButton: {
                defaultProps: {
                    disableRipple: true,
                    sx: {
                        width: "100%",
                        p: 2,
                    },
                },
            },
            MuiTimePickerToolbar: {
                styleOverrides: {
                    hourMinuteLabel: {
                        alignItems: "center",
                        maxWidth: "100vw",
                    },
                },
            },
            MuiPickersLayout: {
                styleOverrides: {
                    actionBar: {
                        maxWidth: "100vw",
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        maxWidth: "100vw",
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    filledSuccess: {
                        color: "white",
                    }
                },
            },
        },
        cssVarPrefix: "",
    },
    {
        // CSS variables that are not automatically created. You can see the variables on the html element.
        "font-normal": typography.body1.fontFamily,
    }
);
